/**
 * AuthedApp.tsx — the authed-shell view switch (T-649, ruling A).
 *
 * The router-less SPA now has two top-level authed views. This component sits between RequireAuth
 * and the views and picks one from useAppView (the #admin[/section] discriminator): the admin PAGE
 * for an admin, the map otherwise. It is the exact seam my package proposed — no router, no infra
 * change, mount-exclusive so only one view (and so only one History owner) is ever live.
 *
 * Gating mirrors the three layers the old dialog had: the entry is only offered to admins (TopBar,
 * via MapShell's onOpenAdmin), the page only renders for user.isAdmin here, and the 8 admin routes
 * enforce requireRole('admins') server-side — the real boundary, untouched. A non-admin who
 * deep-links or hand-types #admin falls back to the map (the same effect as the dialog never
 * mounting), and the misleading hash is normalised away.
 */
import { useEffect } from 'react'
import { useAuth } from '../auth/useAuth'
import { useAppView } from '../hooks/useAppView'
import { MapShell } from './MapShell'
import { AdminPage } from './AdminPage'

export function AuthedApp() {
  const { user } = useAuth()
  const isAdmin = user?.isAdmin ?? false
  const { view, section, navigateToAdmin, selectSection, navigateToMap } = useAppView()

  // A non-admin can only reach view === 'admin' by deep-link or by hand-typing the hash — never
  // through UI, since the entry is admin-only. Fall back to the map and strip the misleading hash.
  // Server RBAC would refuse the data regardless; this keeps the client honest about which view it is.
  useEffect(() => {
    if (view === 'admin' && !isAdmin) navigateToMap()
  }, [view, isAdmin, navigateToMap])

  if (view === 'admin' && isAdmin) {
    return (
      <AdminPage section={section} onSelectSection={selectSection} onBackToMap={navigateToMap} />
    )
  }
  return <MapShell onOpenAdmin={() => navigateToAdmin()} />
}
